import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Send, RefreshCw, Plus, X, CheckCircle, Mail, Play, Pause, Edit3, ShieldCheck, ShieldAlert, AlertTriangle, Users, Clock, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { API_URL, fetchWithAuth } from './constants';
import { toast } from 'sonner';

const WARNING_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', iconType: 'critical', iconColor: 'text-red-500', text: 'text-red-700' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', iconType: 'warning', iconColor: 'text-amber-500', text: 'text-amber-700' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', iconType: 'info', iconColor: 'text-blue-500', text: 'text-blue-600' },
};

const WarningIcon = ({ type, className }) => {
  if (type === 'critical') return <ShieldAlert className={className} />;
  if (type === 'warning') return <AlertTriangle className={className} />;
  return <Info className={className} />;
};

const ActivationModal = ({ campaignId, onClose, onActivated }) => {
  const [loading, setLoading] = useState(true);
  const [preCheck, setPreCheck] = useState(null);
  const [activating, setActivating] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [showSequence, setShowSequence] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchWithAuth(
          `${API_URL}/api/intelligence/acquisition/campaigns/${campaignId}/pre-activate`
        );
        if (cancelled) return;
        if (data.error) {
          toast.error(data.detail || 'Failed to load pre-activation data');
          onClose();
          return;
        }
        setPreCheck(data);
      } catch {
        if (!cancelled) { toast.error('Failed to load campaign details'); onClose(); }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [campaignId, onClose]);

  const handleActivate = async () => {
    if (!confirmed) return;
    setActivating(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(
        `${API_URL}/api/intelligence/acquisition/campaigns/${campaignId}/activate`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        toast.success('Campaign activated successfully');
        onActivated();
        onClose();
      } else {
        toast.error(data.detail || 'Activation failed');
      }
    } catch {
      toast.error('Campaign activation failed');
    }
    setActivating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="activation-modal"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0A1F44]">Activate Campaign</h2>
              <p className="text-[10px] text-gray-500">Review before sending emails</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-activation-modal">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading campaign details...
          </div>
        )}

        {!loading && preCheck && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Campaign Name */}
              <div className="p-3 bg-[#0A1F44]/5 rounded-lg border border-[#0A1F44]/10">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-0.5">Campaign</p>
                <p className="text-sm font-bold text-[#0A1F44]">{preCheck.campaign_name}</p>
                {preCheck.template_name && (
                  <p className="text-[10px] text-gray-500 mt-0.5">Template: {preCheck.template_name}</p>
                )}
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                  <Users className="h-4 w-4 text-[#D4AF37] mx-auto mb-1" />
                  <p className="text-lg font-bold text-[#0A1F44]">{preCheck.leads_in_campaign}</p>
                  <p className="text-[9px] text-gray-500">Companies Queued</p>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                  <Mail className="h-4 w-4 text-[#D4AF37] mx-auto mb-1" />
                  <p className="text-[11px] font-bold text-[#0A1F44] truncate" title={preCheck.email_account}>
                    {preCheck.email_account}
                  </p>
                  <p className="text-[9px] text-gray-500">Sending Account</p>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                  <Clock className="h-4 w-4 text-[#D4AF37] mx-auto mb-1" />
                  <p className="text-[11px] font-bold text-[#0A1F44]">{preCheck.daily_limit}</p>
                  <p className="text-[9px] text-gray-500">Daily Limit</p>
                </div>
              </div>

              {/* Schedule */}
              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">Sending Schedule</p>
                <div className="flex items-center gap-4 text-xs text-[#0A1F44]">
                  <span><strong>Timezone:</strong> {preCheck.schedule.timezone}</span>
                  <span><strong>Hours:</strong> {preCheck.schedule.hours}</span>
                  <span><strong>Days:</strong> {preCheck.schedule.days_active} active</span>
                </div>
              </div>

              {/* Sequence Preview */}
              {preCheck.sequence_count > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button onClick={() => setShowSequence(prev => !prev)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                    data-testid="toggle-sequence-preview">
                    <div className="flex items-center gap-2">
                      <Send className="h-3.5 w-3.5 text-[#0A1F44]" />
                      <span className="text-xs font-bold text-[#0A1F44]">
                        Email Sequence ({preCheck.sequence_count} steps)
                      </span>
                    </div>
                    {showSequence
                      ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
                      : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                  </button>
                  {showSequence && (
                    <div className="px-4 py-3 space-y-3 bg-white">
                      {preCheck.sequence_steps.map((step, i) => (
                        <div key={i} className="flex gap-3" data-testid={`sequence-step-${i}`}>
                          <div className="flex flex-col items-center">
                            <div className="h-6 w-6 rounded-full bg-[#0A1F44] text-white text-[10px] font-bold flex items-center justify-center">
                              {step.step_number}
                            </div>
                            {i < preCheck.sequence_steps.length - 1 && (
                              <div className="w-px flex-1 bg-gray-200 mt-1" />
                            )}
                          </div>
                          <div className="flex-1 pb-2">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-semibold text-[#0A1F44]">{step.subject}</span>
                              {step.variant_count > 1 && (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-bold rounded">
                                  A/B
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-500 leading-relaxed">{step.body_preview}</p>
                            {step.delay > 0 && (
                              <p className="text-[9px] text-gray-400 mt-1">
                                Wait {step.delay} {step.delay_unit} before next step
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Warnings */}
              {preCheck.warnings.length > 0 && (
                <div className="space-y-2" data-testid="activation-warnings">
                  {preCheck.warnings.map((w, i) => {
                    const style = WARNING_STYLES[w.type] || WARNING_STYLES.info;
                    return (
                      <div key={i}
                        className={`flex items-start gap-2.5 p-3 rounded-lg border ${style.bg} ${style.border}`}
                        data-testid={`warning-${w.type}-${i}`}>
                        <WarningIcon type={style.iconType} className={`h-4 w-4 flex-shrink-0 mt-0.5 ${style.iconColor}`} />
                        <p className={`text-xs leading-relaxed ${style.text}`}>{w.message}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Confirmation Checkbox */}
              <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                data-testid="confirm-checkbox-label">
                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="confirm-activation-checkbox" />
                <span className="text-xs text-[#0A1F44] leading-relaxed">
                  I have reviewed the campaign details, email sequence, and sending schedule.
                  I confirm I want to <strong>activate this campaign and begin sending emails</strong> to
                  the contacts listed.
                </span>
              </label>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-xl">
              <button onClick={onClose}
                className="px-4 py-2 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
                data-testid="cancel-activation-btn">
                Cancel
              </button>
              <button onClick={handleActivate}
                disabled={activating || !confirmed || preCheck.has_critical_warnings}
                className={`px-6 py-2 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all disabled:opacity-50 ${
                  confirmed && !preCheck.has_critical_warnings
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="confirm-activate-btn">
                {activating
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Activating...</>
                  : <><Play className="h-3.5 w-3.5" /> Activate Campaign</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const CampaignPanel = ({ campaigns, loading, onRefresh, onCreateCampaign, selectedCampaign, setSelectedCampaign, onActivate, onPause, analytics, loadingAnalytics, onManageTemplates }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activatingCampaignId, setActivatingCampaignId] = useState(null);

  const loadTemplates = useCallback(() => {
    fetchWithAuth(`${API_URL}/api/intelligence/acquisition/sequence-templates`)
      .then(data => setTemplates(data.templates || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    await onCreateCampaign(newName.trim(), selectedTemplate?.id);
    setNewName('');
    setSelectedTemplate(null);
    setShowCreate(false);
    setCreating(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="campaign-panel">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-[#D4AF37]" />
          <h3 className="text-sm font-bold text-[#0A1F44]">Instantly.ai Campaigns</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid="refresh-campaigns-btn">
            <RefreshCw className={`h-3.5 w-3.5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#0A1F44] text-white text-xs font-medium rounded-lg hover:bg-[#0d2a5c] transition-colors"
            data-testid="new-campaign-btn">
            <Plus className="h-3 w-3" /> New Campaign
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 space-y-3" data-testid="create-campaign-form">
          <div className="flex items-center gap-2">
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="Campaign name..."
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
              data-testid="campaign-name-input"
              onKeyDown={e => e.key === 'Enter' && handleCreate()} />
            <button onClick={handleCreate} disabled={creating || !newName.trim()}
              className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-semibold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1"
              data-testid="create-campaign-submit">
              {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create
            </button>
            <button onClick={() => { setShowCreate(false); setSelectedTemplate(null); }} className="p-1.5 hover:bg-gray-200 rounded-lg">
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          </div>
          <div data-testid="template-selection">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">Email Sequence Template</p>
            <div className="grid grid-cols-1 gap-1.5">
              {templates.map(t => (
                <div key={t.id}
                  onClick={() => setSelectedTemplate(selectedTemplate?.id === t.id ? null : t)}
                  className={`p-2.5 rounded-lg border cursor-pointer transition-all ${selectedTemplate?.id === t.id ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                  data-testid={`template-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${selectedTemplate?.id === t.id ? 'bg-[#D4AF37]' : 'bg-gray-200'}`}>
                        {selectedTemplate?.id === t.id ? <CheckCircle className="h-3 w-3 text-white" /> : <Mail className="h-3 w-3 text-gray-500" />}
                      </div>
                      <div className="min-w-0">
                        <span className="text-xs font-medium text-[#0A1F44] block truncate">{t.name}</span>
                        <span className="text-[10px] text-gray-400">{t.description}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded">{t.steps_count} steps</span>
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded">{t.total_days}d</span>
                    </div>
                  </div>
                </div>
              ))}
              <div
                onClick={() => setSelectedTemplate(null)}
                className={`p-2.5 rounded-lg border cursor-pointer transition-all ${!selectedTemplate ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                data-testid="template-none">
                <div className="flex items-center gap-2">
                  <div className={`h-5 w-5 rounded-full flex items-center justify-center ${!selectedTemplate ? 'bg-[#D4AF37]' : 'bg-gray-200'}`}>
                    {!selectedTemplate ? <CheckCircle className="h-3 w-3 text-white" /> : <X className="h-3 w-3 text-gray-500" />}
                  </div>
                  <span className="text-xs font-medium text-gray-600">No template (blank campaign)</span>
                </div>
              </div>
              <button type="button" onClick={onManageTemplates}
                className="w-full mt-1 flex items-center justify-center gap-1 px-3 py-1.5 text-[10px] font-medium text-[#0A1F44] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="manage-templates-btn">
                <Edit3 className="h-3 w-3" /> Manage & Create Templates
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-5">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading campaigns...
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-6">
            <Send className="h-8 w-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No campaigns yet. Create one to start outreach.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => {
              const isSelected = selectedCampaign?.id === c.id;
              const status = c.status === 1 ? 'Active' : c.status === 2 ? 'Paused' : c.status === 0 ? 'Draft' : 'Unknown';
              const statusColor = status === 'Active' ? 'text-green-600 bg-green-50' : status === 'Paused' ? 'text-amber-600 bg-amber-50' : 'text-gray-500 bg-gray-50';
              return (
                <div key={c.id}
                  onClick={() => setSelectedCampaign(isSelected ? null : c)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/5 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`campaign-item-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="h-4 w-4 text-[#0A1F44]/50 flex-shrink-0" />
                      <span className="text-sm font-medium text-[#0A1F44] truncate">{c.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColor}`}>{status}</span>
                      {isSelected && status !== 'Active' && (
                        <button onClick={(e) => { e.stopPropagation(); setActivatingCampaignId(c.id); }}
                          className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          title="Activate Campaign" data-testid={`activate-${c.id}`}>
                          <Play className="h-3 w-3" />
                        </button>
                      )}
                      {isSelected && status === 'Active' && (
                        <button onClick={(e) => { e.stopPropagation(); onPause(c.id); }}
                          className="p-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors"
                          title="Pause Campaign" data-testid={`pause-${c.id}`}>
                          <Pause className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  {isSelected && analytics && (
                    <div className="mt-3 pt-3 border-t border-gray-200/50 grid grid-cols-4 gap-3" data-testid="campaign-analytics">
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#0A1F44]">{analytics.contacted}</p>
                        <p className="text-[10px] text-gray-400">Contacted</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-600">{analytics.opened}</p>
                        <p className="text-[10px] text-gray-400">Opened ({analytics.open_rate}%)</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{analytics.replied}</p>
                        <p className="text-[10px] text-gray-400">Replied ({analytics.reply_rate}%)</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-red-500">{analytics.bounced}</p>
                        <p className="text-[10px] text-gray-400">Bounced ({analytics.bounce_rate}%)</p>
                      </div>
                    </div>
                  )}
                  {isSelected && loadingAnalytics && (
                    <div className="mt-3 pt-3 border-t border-gray-200/50 text-center text-xs text-gray-400">
                      <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Loading analytics...
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {activatingCampaignId && (
        <ActivationModal
          campaignId={activatingCampaignId}
          onClose={() => setActivatingCampaignId(null)}
          onActivated={() => {
            const cid = activatingCampaignId;
            setActivatingCampaignId(null);
            onActivate(cid);
            onRefresh();
          }}
        />
      )}
    </div>
  );
};
